/**
 * Settings → Invites section. Three things:
 *   - Budget readout: "You have N invites left"
 *   - Form to mint a new invite — optional email (sends template email)
 *     or plain link (copy-pasteable, no email)
 *   - List of the user's invite history with copy-link / revoke actions
 */
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'motion/react';
import { Copy, Check, Mail, Link2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Form, FormTextField } from '@/interfaces/learner/ui';
import {
  useInviteBudget,
  useMyInvites,
  useGenerateInvite,
  useRevokeMyInvite,
  type MyInvite,
} from '@/shared/api/hooks';
import { SettingsSection } from './SettingsSection';

// Match the format we emit server-side, but accept blank (no recipient).
const formSchema = z.object({
  email: z
    .string()
    .trim()
    .optional()
    .refine(
      (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
      'Enter a valid email or leave blank for a link-only invite'
    ),
});
type FormValues = z.infer<typeof formSchema>;

function inviteUrl(code: string): string {
  return `${window.location.origin}/accept-invitation?code=${encodeURIComponent(code)}`;
}

function statusBadge(status: MyInvite['status']) {
  switch (status) {
    case 'pending':
      return <Badge variant="info">Pending</Badge>;
    case 'redeemed':
      return <Badge variant="success">Redeemed</Badge>;
    case 'revoked':
      return <Badge variant="muted">Revoked</Badge>;
  }
}

export function InvitesSection() {
  const { data: budget = 0, isLoading: budgetLoading } = useInviteBudget();
  const { data: invites, isLoading: invitesLoading } = useMyInvites();
  const generate = useGenerateInvite();
  const revoke = useRevokeMyInvite();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  // Tracks which invite row was just copied — used to flip the icon to a
  // checkmark for ~1.5s as visible feedback. Avoids a global "copied"
  // state which would briefly tick every row at once.
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Sort: pending first (most actionable), then redeemed, then revoked.
  // Within a status, newest first.
  const sortedInvites = useMemo(() => {
    if (!invites) return [];
    const order: Record<MyInvite['status'], number> = {
      pending: 0,
      redeemed: 1,
      revoked: 2,
    };
    return [...invites].sort((a, b) => {
      if (order[a.status] !== order[b.status]) {
        return order[a.status] - order[b.status];
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [invites]);

  const outOfBudget = !budgetLoading && budget <= 0;

  async function handleCopy(invite: MyInvite) {
    try {
      await navigator.clipboard.writeText(inviteUrl(invite.code));
      setCopiedId(invite.id);
      toast.success('Invite link copied');
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }

  function onSubmit(data: FormValues) {
    generate.mutate(
      { invitedEmail: data.email || undefined },
      {
        onSuccess: () => form.reset({ email: '' }),
      }
    );
  }

  return (
    <SettingsSection title="Invites" delay={0.25} cardClassName="space-y-6">
      {/* Budget summary */}
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-list-title">
            {budgetLoading
              ? 'Loading…'
              : budget === 1
                ? 'You have 1 invite left'
                : `You have ${budget} invites left`}
          </p>
          <p className="text-meta-muted mt-0.5">
            Share Formency with friends. Invites expire after 30 days if unused; revoking a pending
            invite refunds your budget.
          </p>
        </div>
      </div>

      {/* Generate form */}
      <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
        <div className="max-w-md">
          <FormTextField
            control={form.control}
            name="email"
            label="Send to email (optional)"
            placeholder="friend@example.com"
            type="email"
            description="Leave blank to generate a link you can share manually"
            isDisabled={outOfBudget || generate.isPending}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="accent" isDisabled={outOfBudget || generate.isPending}>
            {generate.isPending
              ? 'Generating…'
              : form.watch('email')
                ? 'Send invite email'
                : 'Generate invite link'}
          </Button>
          {outOfBudget && (
            <span
              className="text-meta-muted self-center"
              style={{ color: 'var(--color-warning-text)' }}
            >
              You&apos;ve used all your invites — ask an admin to top up.
            </span>
          )}
        </div>
      </Form>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* History */}
      <div>
        <h3 className="text-list-title mb-3">Your invites</h3>
        {invitesLoading ? (
          <p className="text-meta-muted">Loading…</p>
        ) : sortedInvites.length === 0 ? (
          <p className="text-meta-muted">You haven&apos;t created any invites yet.</p>
        ) : (
          <ul className="space-y-2">
            {sortedInvites.map((invite, i) => (
              <motion.li
                key={invite.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.02 }}
                className="flex items-center gap-3 rounded-2xl border px-4 py-3"
                style={{
                  background: 'var(--color-surface-muted)',
                  borderColor: 'var(--color-surface-border)',
                }}
              >
                <div
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{
                    background: 'var(--color-surface-solid)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {invite.invitedEmail ? (
                    <Mail size={14} strokeWidth={1.75} />
                  ) : (
                    <Link2 size={14} strokeWidth={1.75} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-sm font-medium truncate"
                      title={invite.invitedEmail ?? undefined}
                    >
                      {invite.invitedEmail ?? 'Link only'}
                    </span>
                    {statusBadge(invite.status)}
                  </div>
                  <div className="text-meta-muted mt-0.5 font-mono text-xs">{invite.code}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {invite.status === 'pending' && (
                    <>
                      <Button variant="ghost" size="sm" onPress={() => handleCopy(invite)}>
                        {copiedId === invite.id ? (
                          <span className="flex items-center gap-1.5">
                            <Check size={14} strokeWidth={2} />
                            Copied
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <Copy size={14} strokeWidth={2} />
                            Copy link
                          </span>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() => revoke.mutate(invite.id)}
                        isDisabled={revoke.isPending}
                      >
                        <Trash2
                          size={14}
                          strokeWidth={2}
                          style={{ color: 'var(--color-error-text)' }}
                        />
                      </Button>
                    </>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </SettingsSection>
  );
}

export default InvitesSection;
